// ============================================================
// VCS.AudioMonitor — Voice-Level-Meter & Knock-Ton
// ============================================================
window.VCS = window.VCS || {};

VCS.AudioMonitor = class {

    // ── Konstanten ───────────────────────────────────────────

    // EMA-Koeffizient für Voice-Level-Glättung
    static EMA_DECAY  = 0.82;
    static EMA_WEIGHT = 0.18;

    // Schwelle ab der "speaking" gilt
    static SPEAKING_THRESHOLD = 0.03;

    // Knock-Ton: Grundfrequenz, Offsets, Hüllkurve
    static KNOCK_FREQ     = 880;
    static KNOCK_OFFSETS  = [0, 0.2];
    static KNOCK_INTERVAL = 1800;
    static KNOCK_TIMEOUT  = 30000;

    // ── Konstruktor ──────────────────────────────────────────

    constructor() {
        // View-Cache
        this._view = {
            btnRingToggle:   $('#btnRingToggle'),
            approvalOverlay: $('#approvalOverlay'),
            preJoinScreen:   $('#preJoinScreen'),
            tabCall:         $('#tabCall'),
            appShell:        $('#appShell'),
            remoteCamOff:    $('#remoteCamOff'),
            previewCamOff:   $('#previewCamOff'),
        };
    }

    // ── Ring / Approval ──────────────────────────────────────

    updateRingToggleUI() {
        if (!this._view.btnRingToggle.length) {
            return;
        }
        const enabled = VCS.state.ringEnabled;
        VCS.ui?.setButtonIcon?.('btnRingToggle', enabled ? 'bell' : 'bellOff');
        this._view.btnRingToggle
            .toggleClass('btn-active-red', !enabled)
            .attr('title', enabled ? 'Klingeln an' : 'Klingeln aus');
    }

    setApprovalRinging(active) {
        this._view.approvalOverlay.children().first()
            .toggleClass('approval-ringing', active);
    }

    // ── Knock-Ton ────────────────────────────────────────────

    startIncomingKnock() {
        this.stopIncomingKnock();
        if (!VCS.state.ringEnabled) {
            return;
        }

        this.setApprovalRinging(true);
        VCS.ui?.showToast?.(t('incomingRequest'), 'warning', 2500);

        this._playKnockTone();
        navigator.vibrate?.([120, 80, 140]);

        VCS.state.knockInterval = setInterval(() => {
            this._playKnockTone();
            navigator.vibrate?.([80, 60, 80]);
        }, VCS.AudioMonitor.KNOCK_INTERVAL);

        // Endlos-Ringing vermeiden, falls der Host nicht reagiert
        VCS.state.knockStopTimer = setTimeout(
            () => this.stopIncomingKnock(),
            VCS.AudioMonitor.KNOCK_TIMEOUT
        );
    }

    stopIncomingKnock() {
        if (VCS.state.knockInterval) {
            clearInterval(VCS.state.knockInterval);
            VCS.state.knockInterval = null;
        }
        if (VCS.state.knockStopTimer) {
            clearTimeout(VCS.state.knockStopTimer);
            VCS.state.knockStopTimer = null;
        }
        navigator.vibrate?.(0);
        this.setApprovalRinging(false);
    }

    // ── Remote Voice Monitor ─────────────────────────────────

    startRemote(stream) {
        this.stopRemote();
        if (!stream) {
            return;
        }
        const ctx = this._createAudioCtx();
        if (!ctx) {
            return;
        }
        VCS.state.remoteVoiceAudioCtx = ctx;

        void this._startMonitor(ctx, stream, {
            sourceKey:  'remoteVoiceSourceNode',
            analyserKey:'remoteVoiceAnalyser',
            dataKey:    'remoteVoiceData',
            emaKey:     'remoteVoiceLevelEma',
            rafKey:     'remoteVoiceRaf',
            stopFn:     () => this.stopRemote(),
            speakingFn: (rms) => {
                const camOffVisible = !this._view.remoteCamOff.hasClass('hidden');
                const speaking = camOffVisible && !VCS.state.remoteMuted && rms > VCS.AudioMonitor.SPEAKING_THRESHOLD;
                this._setRemoteSpeaking(speaking);
            },
        });
    }

    stopRemote() {
        this._stopMonitor({
            rafKey:     'remoteVoiceRaf',
            sourceKey:  'remoteVoiceSourceNode',
            analyserKey:'remoteVoiceAnalyser',
            audioCtxKey:'remoteVoiceAudioCtx',
            dataKey:    'remoteVoiceData',
            emaKey:     'remoteVoiceLevelEma',
        });
        this._setRemoteSpeaking(false);
    }

    // ── Preview Voice Monitor ────────────────────────────────

    startPreview(stream) {
        this.stopPreview();
        if (!stream || !stream.getAudioTracks().length) {
            return;
        }
        const ctx = this._createAudioCtx();
        if (!ctx) {
            return;
        }
        VCS.state.previewVoiceAudioCtx = ctx;

        void this._startMonitor(ctx, stream, {
            sourceKey:  'previewVoiceSourceNode',
            analyserKey:'previewVoiceAnalyser',
            dataKey:    'previewVoiceData',
            emaKey:     'previewVoiceLevelEma',
            rafKey:     'previewVoiceRaf',
            stopFn:     () => this.stopPreview(),
            speakingFn: (rms) => {
                const preJoinVisible = !this._view.preJoinScreen.hasClass('hidden')
                    || (!this._view.tabCall.hasClass('hidden') && !this._view.appShell.hasClass('hidden'));
                const camOffVisible  = !this._view.previewCamOff.hasClass('hidden');
                const speaking = preJoinVisible && camOffVisible
                    && !VCS.state.audioMuted && rms > VCS.AudioMonitor.SPEAKING_THRESHOLD;
                this._setPreviewSpeaking(speaking);
            },
        });
    }

    stopPreview() {
        this._stopMonitor({
            rafKey:     'previewVoiceRaf',
            sourceKey:  'previewVoiceSourceNode',
            analyserKey:'previewVoiceAnalyser',
            audioCtxKey:'previewVoiceAudioCtx',
            dataKey:    'previewVoiceData',
            emaKey:     'previewVoiceLevelEma',
        });
        this._setPreviewSpeaking(false);
    }

    setRemoteCamOffSpeaking(active) {
        const avatar = document.querySelector('#remoteCamOff .call-cam-off-avatar');
        if (!avatar) {
            return;
        }
        avatar.classList.toggle('call-cam-off-avatar--speaking', !!active);
    }

    // ── Init ─────────────────────────────────────────────────

    init() {
        // AudioContext beim ersten User-Gesture entsperren
        const unlock = async () => {
            const ctx = VCS.state.knockAudioCtx;
            if (ctx?.state === 'suspended') {
                await ctx.resume().catch(() => {});
            }
        };
        ['pointerdown', 'touchstart', 'keydown'].forEach((evt) => {
            document.addEventListener(evt, unlock, { once: true, passive: true });
        });
    }

    // ── Private Methoden ─────────────────────────────────────

    _createAudioCtx() {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) {
            return null;
        }
        try {
            return new AudioCtx();
        } catch (_) {
            return null;
        }
    }

    _ensureKnockCtx() {
        if (VCS.state.knockAudioCtx) {
            return VCS.state.knockAudioCtx;
        }
        const ctx = this._createAudioCtx();
        if (ctx) {
            VCS.state.knockAudioCtx = ctx;
        }
        return ctx;
    }

    _playKnockTone() {
        const ctx = this._ensureKnockCtx();
        if (!ctx || ctx.state !== 'running') {
            return;
        }
        const now = ctx.currentTime;
        VCS.AudioMonitor.KNOCK_OFFSETS.forEach((offset) => {
            const osc  = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'sine';
            osc.frequency.setValueAtTime(VCS.AudioMonitor.KNOCK_FREQ, now + offset);
            gain.gain.setValueAtTime(0.0001, now + offset);
            gain.gain.exponentialRampToValueAtTime(0.15,   now + offset + 0.01);
            gain.gain.exponentialRampToValueAtTime(0.0001, now + offset + 0.14);
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start(now + offset);
            osc.stop(now + offset + 0.16);
        });
    }

    async _startMonitor(ctx, stream, opts) {
        if (ctx.state === 'suspended') {
            await ctx.resume().catch(() => {});
        }
        if (!ctx || ctx.state === 'closed') {
            return;
        }

        const s = VCS.state;
        try {
            s[opts.sourceKey]   = ctx.createMediaStreamSource(stream);
            s[opts.analyserKey] = ctx.createAnalyser();
            s[opts.analyserKey].fftSize = 1024;
            s[opts.analyserKey].smoothingTimeConstant = 0.75;
            s[opts.dataKey]     = new Uint8Array(s[opts.analyserKey].fftSize);
            s[opts.sourceKey].connect(s[opts.analyserKey]);
        } catch (_) {
            opts.stopFn();
            return;
        }

        const tick = () => {
            if (!s[opts.analyserKey] || !s[opts.dataKey]) {
                return;
            }
            s[opts.analyserKey].getByteTimeDomainData(s[opts.dataKey]);
            let sum = 0;
            for (let i = 0; i < s[opts.dataKey].length; i++) {
                const centered = (s[opts.dataKey][i] - 128) / 128;
                sum += centered * centered;
            }
            const rms = Math.sqrt(sum / s[opts.dataKey].length);
            s[opts.emaKey] = (s[opts.emaKey] * VCS.AudioMonitor.EMA_DECAY)
                           + (rms * VCS.AudioMonitor.EMA_WEIGHT);

            opts.speakingFn(s[opts.emaKey]);
            s[opts.rafKey] = requestAnimationFrame(tick);
        };
        tick();
    }

    _stopMonitor(keys) {
        const s = VCS.state;

        if (s[keys.rafKey]) {
            cancelAnimationFrame(s[keys.rafKey]);
            s[keys.rafKey] = null;
        }
        if (s[keys.sourceKey]) {
            try { s[keys.sourceKey].disconnect(); } catch (_) {}
            s[keys.sourceKey] = null;
        }
        if (s[keys.analyserKey]) {
            try { s[keys.analyserKey].disconnect(); } catch (_) {}
            s[keys.analyserKey] = null;
        }
        if (s[keys.audioCtxKey]) {
            s[keys.audioCtxKey].close().catch(() => {});
            s[keys.audioCtxKey] = null;
        }
        s[keys.dataKey] = null;
        s[keys.emaKey]  = 0;
    }

    _setRemoteSpeaking(active) {
        this._view.remoteCamOff.find('.call-cam-off-avatar')
            .toggleClass('call-cam-off-avatar--speaking', !!active);
    }

    _setPreviewSpeaking(active) {
        this._view.previewCamOff.find('.prejoin-cam-off-circle')
            .toggleClass('prejoin-cam-off-circle--speaking', !!active);
    }
};
